import {
  ActivityIcon,
  BookOpenIcon,
  BoxesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  FileIcon,
  FileTextIcon,
  HeartHandshakeIcon,
  LayoutDashboardIcon,
  LifeBuoyIcon,
  PackageIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  PhoneIcon,
  UploadIcon,
  UsersIcon,
  type LucideIcon
} from 'lucide-react';

import type { UserRole } from '@/components/auth/user-provider';

/** Roles that can open operational school screens. */
export const STAFF_ROLES: readonly UserRole[] = ['admin', 'teacher'];

/** Roles that can open administration screens. */
export const ADMIN_ROLES: readonly UserRole[] = ['admin'];

/** A leaf link nested under a parent nav item (rendered as a sub heading). */
export type NavChild = {
  title: string;
  to: string;
  /** If set, only these roles see the child link. */
  roles?: readonly UserRole[];
};

/**
 * A sidebar entry: a direct link (`to`), an external link (`href`), or an
 * expandable group (`children`).
 */
export type NavItem = {
  title: string;
  icon: LucideIcon;
  to?: string;
  href?: string;
  external?: boolean;
  children?: NavChild[];
  /** If set, only these roles see the item. Defaults to staff when omitted on main nav. */
  roles?: readonly UserRole[];
};

/** A titled section of the sidebar (rendered as a heading). */
export type NavSection = {
  heading: string;
  items: NavItem[];
  /** If set, only these roles see the section. */
  roles?: readonly UserRole[];
};

function roleAllowed(
  allowed: readonly UserRole[] | undefined,
  role: UserRole | undefined
): boolean {
  if (!role) return false;
  if (!allowed || allowed.length === 0) return true;
  return allowed.includes(role);
}

/** Filter nav config down to entries the current role may open. */
export function filterNavigationForRole(
  sections: NavSection[],
  role: UserRole | undefined
): NavSection[] {
  if (!role) return [];

  return sections
    .filter((section) => roleAllowed(section.roles, role))
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => roleAllowed(item.roles, role))
        .map((item) => ({
          ...item,
          children: item.children?.filter((child) => roleAllowed(child.roles, role))
        }))
        .filter((item) => !item.children || item.children.length > 0 || Boolean(item.to || item.href))
    }))
    .filter((section) => section.items.length > 0);
}

/** Main (non-admin) navigation — staff only. */
export const mainNavigation: NavSection[] = [
  {
    heading: 'Overview',
    roles: STAFF_ROLES,
    items: [
      {
        title: 'Dashboard',
        icon: LayoutDashboardIcon,
        to: '/dashboard',
        roles: STAFF_ROLES
      }
    ]
  },
  {
    heading: 'School',
    roles: STAFF_ROLES,
    items: [
      { title: 'Events', icon: CalendarDaysIcon, to: '/events', roles: STAFF_ROLES },
      { title: 'Students', icon: UsersIcon, to: '/students', roles: STAFF_ROLES },
      { title: 'Volunteers', icon: HeartHandshakeIcon, to: '/volunteers', roles: STAFF_ROLES },
      { title: 'Audit Log', icon: ScrollTextIcon, to: '/audit', roles: STAFF_ROLES }
    ]
  }
];

/**
 * Horizontal top menu rendered in the header, next to the site logo.
 * Keep this short — deep navigation belongs in the sidebar.
 */
export const topNavigation: NavItem[] = [
  {
    title: 'Dashboard',
    icon: LayoutDashboardIcon,
    to: '/dashboard',
    roles: STAFF_ROLES
  },
  {
    title: 'Events',
    icon: CalendarDaysIcon,
    to: '/events',
    roles: STAFF_ROLES
  }
];

/**
 * Admin navigation — kept separate from the user menus and only rendered
 * for users with the `admin` role.
 */
/** Single sidebar entry into the admin area (sub-pages live in AdminLayout). */
export const adminNavigation: NavSection = {
  heading: 'Administration',
  roles: ADMIN_ROLES,
  items: [
    {
      title: 'Admin',
      icon: ShieldIcon,
      to: '/admin',
      roles: ADMIN_ROLES
    }
  ]
};

/** Paths that keep the sidebar Admin link highlighted. */
export function isAdminAreaPath(pathname: string): boolean {
  return (
    pathname === '/admin' ||
    pathname.startsWith('/admin/') ||
    pathname === '/settings' ||
    pathname.startsWith('/settings/') ||
    pathname === '/approved-numbers' ||
    pathname.startsWith('/approved-numbers/')
  );
}

export type Breadcrumb = {
  section: string;
  item: string;
  child?: string;
};

const allSections: NavSection[] = [...mainNavigation, adminNavigation];

/** Resolve the breadcrumb trail for a pathname from the nav config. */
export function findBreadcrumb(pathname: string): Breadcrumb | undefined {
  for (const section of allSections) {
    for (const item of section.items) {
      if (item.to === pathname) {
        return { section: section.heading, item: item.title };
      }
      const child = item.children?.find((entry) => entry.to === pathname);
      if (child) {
        return { section: section.heading, item: item.title, child: child.title };
      }
      // Direct links with nested routes (e.g. /settings/appearance) still resolve
      // to their parent entry.
      if (item.to && item.to !== '/dashboard' && pathname.startsWith(`${item.to}/`)) {
        const nestedChild = item.children?.find(
          (entry) => entry.to === pathname || pathname.startsWith(`${entry.to}/`)
        );
        return {
          section: section.heading,
          item: item.title,
          child: nestedChild?.title
        };
      }
    }
  }

  // Settings / approved numbers live under admin area chrome but use their own paths.
  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    return {
      section: 'Administration',
      item: 'Admin',
      child: pathname === '/settings/attendance' ? 'Attendance register' : 'Settings'
    };
  }
  if (pathname === '/approved-numbers' || pathname.startsWith('/approved-numbers/')) {
    return {
      section: 'Administration',
      item: 'Admin',
      child: 'Approved numbers'
    };
  }

  return undefined;
}

/**
 * Page icon registry — the single source of truth for icons used in both
 * the nav (sidebar/top-nav) and the PageHeader banner on each page.
 *
 * Rule: never import a page header icon directly from 'lucide-react'.
 * Always import it from here so nav and page stay in sync automatically.
 */
export {
  ActivityIcon,
  BookOpenIcon,
  BoxesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  FileIcon,
  FileTextIcon,
  HeartHandshakeIcon,
  LayoutDashboardIcon,
  LifeBuoyIcon,
  PackageIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  PhoneIcon,
  UploadIcon,
  UsersIcon
};
